/**
 * `useReducedMotion` — react-friendly wrapper around the
 * `(prefers-reduced-motion: reduce)` media query.
 *
 * The mentor uses this in three places:
 *   - SpotlightRenderer: disables fade-in + smooth-rect transitions
 *   - CoachingCard: disables card slide-in
 *   - Live-region announcer: not currently affected, but the hook is
 *     reused for completeness
 *
 * Returns `true` when the user has explicitly asked the OS for reduced
 * motion. Safe in SSR — defaults to `false` when `window` is absent.
 */

import { useEffect, useState } from "react";

const QUERY = "(prefers-reduced-motion: reduce)";

function initial(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.matchMedia(QUERY).matches;
  } catch {
    return false;
  }
}

export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(initial);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let mql: MediaQueryList;
    try {
      mql = window.matchMedia(QUERY);
    } catch {
      return;
    }
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    // Safari < 14 only supports the deprecated addListener API.
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    }
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, []);

  return reduced;
}
